/**
 * openPrintReport — сборка печатного документа из готовых секций (задачи
 * 4-10) и открытие системного диалога печати браузера.
 *
 * Собирает: шапку `.head` (лого + заголовок отчёта слева, тип записи/дата
 * формирования/id справа — эта разметка не досталась ни одной секции, план
 * забыл её кому-либо поручить, обнаружилось на задаче 5), затем
 * record-info/hero/provenance, затем четыре группы панели (Оценка/Скрипт/
 * Речь/Инсайты — зеркалят `hasScoring`/`hasScript`/`hasSpeech`/`hasInsights`
 * из AnalysisPanel.tsx v2) с разделителем только когда в группе есть непустая
 * секция, затем футер. Состав групп — тоже зеркало: комплаенс и рекомендации
 * живут в «Инсайтах», в «Скрипте» только соблюдение скрипта.
 *
 * `t` резолвит ДВА словаря — `records.json` (`records.analysisPanel.*`,
 * `records.otherCategoryNotice.*`, `records.verificationNotice.*`) и
 * `common.json` (`pdf.*`, `common:reviewQueue.reasons.*`,
 * `common:common.unknown`). `i18n.t.bind(i18n)` из старого printReport.ts
 * НЕ годится буквально: та функция резолвится в единственный `defaultNS`
 * ('common') и живёт только потому, что старый отчёт ни разу не звал ключ
 * из `records.*`. Здесь секции зовут оба словаря без явного namespace-
 * префикса, поэтому `t` явно связан со списком `['records', 'common']` —
 * порядок важен: оба словаря содержат верхнеуровневый ключ `records`
 * (`records.recordTitleFallback` в common.json — "Record", без
 * интерполяции; в records.json — "Record #{{id}}"), и `records` должен
 * стоять первым, иначе фолбэк-заголовок безмолвно теряет `{{id}}`.
 */
import i18n from '@/i18n/config';
import { formatDateShort, formatDateTime } from '@/lib/utils';
import { renderDocument } from './shell';
import { openPrintDocument } from './open-document';
import { AELO_LOCKUP_SVG } from './logo';
import { groupDivider } from './primitives';
import { escapeHtml } from './escape';
import type { PrintReportInput, SectionFn, TFn } from './types';
import { renderRecordInfo } from './sections/record-info';
import { renderVerification } from './sections/verification';
import { renderHero } from './sections/hero';
import { renderProvenance } from './sections/provenance';
import { renderQualityScore } from './sections/quality-score';
import { renderSummary } from './sections/summary';
import { renderContent } from './sections/content';
import { renderDialogue } from './sections/dialogue';
import { renderSaleProbability } from './sections/sale-probability';
import { renderAgreements } from './sections/agreements';
import { renderScriptAdherence } from './sections/script-adherence';
import { renderCriteria } from './sections/criteria';
import { renderCompliance } from './sections/compliance';
import { renderRecommendations } from './sections/recommendations';
import { renderSpeech } from './sections/speech';
import { renderVoiceMetrics } from './sections/voice-metrics';
import { renderEmotional } from './sections/emotional';
import { renderKeyMoments } from './sections/key-moments';
import { renderBusinessIntelligence } from './sections/business-intelligence';

interface Group {
  labelKey: string;
  labelDefault: string;
  sections: SectionFn[];
}

/** Порядок групп и секций внутри них — зеркало AnalysisPanel.tsx v2. */
const GROUPS: Group[] = [
  {
    labelKey: 'records.analysisPanel.groups.scoring',
    labelDefault: 'Score',
    sections: [
      renderQualityScore,
      renderSummary,
      renderContent,
      renderDialogue,
      renderSaleProbability,
      renderAgreements,
    ],
  },
  {
    labelKey: 'records.analysisPanel.groups.script',
    labelDefault: 'Script',
    // Соблюдение скрипта и НИЧЕГО больше — как в панели: комплаенс и
    // рекомендации есть в любом анализе, и пока они лежали здесь, группа
    // «Скрипт» печаталась даже у отчёта, к которому скрипт не привязан.
    sections: [renderScriptAdherence],
  },
  {
    // Своя группа, как в панели: критерии есть и у записи без скрипта, и
    // подмешивать их в «Скрипт» значило бы печатать ту группу без скрипта.
    labelKey: 'records.analysisPanel.groups.criteria',
    labelDefault: 'Criteria',
    sections: [renderCriteria],
  },
  {
    labelKey: 'records.analysisPanel.groups.speech',
    labelDefault: 'Speech',
    sections: [renderSpeech, renderVoiceMetrics, renderEmotional],
  },
  {
    labelKey: 'records.analysisPanel.groups.insights',
    labelDefault: 'Insights',
    sections: [
      renderRecommendations,
      renderKeyMoments,
      renderCompliance,
      renderBusinessIntelligence,
    ],
  },
];

/**
 * Разделитель печатается только когда в группе есть хоть одна непустая
 * секция — считаем это по факту вывода секций, а не заново по данным
 * (иначе гейт неизбежно разъедется с гейтами внутри самих секций).
 */
function renderGroup(group: Group, input: PrintReportInput, t: TFn): string {
  const rendered = group.sections.map((section) => section(input, t)).filter(Boolean);
  if (rendered.length === 0) return '';
  return groupDivider(t(group.labelKey, group.labelDefault)) + rendered.join('');
}

function renderHead(input: PrintReportInput, shortId: string, t: TFn): string {
  const { record } = input;
  return (
    `<div class="head"><div>${AELO_LOCKUP_SVG}<h1>${escapeHtml(t('pdf.title'))}</h1></div>` +
    `<div class="head-meta">` +
    `<div class="brow">${escapeHtml(t(`pdf.channel.${record.type}`))}</div>` +
    `<div>${escapeHtml(t('pdf.generated', { date: formatDateTime(new Date()) }))}</div>` +
    `<div>${escapeHtml(t('pdf.fields.recordId'))} ${escapeHtml(shortId)}…</div>` +
    `</div></div>`
  );
}

function renderFooter(input: PrintReportInput, t: TFn): string {
  const { record } = input;
  // "Неизвестно" вместо прочерка — то же обоснование и тот же ключ, что и
  // в record-info.ts: участник у звонка был всегда, просто имя не
  // разрешилось.
  const participant = record.participantName
    ? escapeHtml(record.participantName)
    : escapeHtml(t('common:common.unknown'));
  const project = escapeHtml(record.projectName ?? record.projectId);
  const date = escapeHtml(formatDateShort(record.createdAt));
  return (
    `<div class="foot"><span>${escapeHtml(t('pdf.footer.brand'))}</span>` +
    `<span>${participant} · ${project} · ${date}</span></div>`
  );
}

export function openPrintReport(input: PrintReportInput): void {
  const lang = i18n.language || 'en';
  // Оба словаря нужны: секции зовут и `records.analysisPanel.*`/
  // `records.otherCategoryNotice.*` (records.json), и `pdf.*`
  // (common.json) — без namespace-префикса на месте вызова. Порядок
  // ['records', 'common'] обязателен, не «для единообразия»: common.json
  // ТОЖЕ содержит верхнеуровневый ключ `records` (с другим
  // `recordTitleFallback` — без интерполяции {{id}}), и он молча
  // перехватит запрос первым, если поставить `common` раньше — заголовок
  // потеряет номер записи без единой упавшей проверки типов или теста
  // (см. src/test/unit/lib/print-report/index.test.ts, тест на
  // records.recordTitleFallback).
  const t = i18n.getFixedT(lang, ['records', 'common']) as TFn;
  const { record } = input;
  // Единственное место, где считаем id записи — renderHead и фолбэк на
  // скачивание ниже переиспользуют это значение вместо пересчёта.
  const shortId = record.id.slice(0, 8);

  const body =
    renderRecordInfo(input, t) +
    // Над оценками, как в панели: предупреждение читают до чисел,
    // которые оно оговаривает.
    renderVerification(input, t) +
    renderHero(input, t) +
    renderProvenance(input, t) +
    GROUPS.map((group) => renderGroup(group, input, t)).join('');

  const recordTitle =
    record.participantName ?? t('records.recordTitleFallback', { id: shortId });

  const html = renderDocument({
    lang,
    title: escapeHtml(`${t('pdf.title')} — ${recordTitle}`),
    header: renderHead(input, shortId, t),
    body,
    footer: renderFooter(input, t),
    // Единственный вызов t(...) в файле без явного escapeHtml был бы
    // исключением из правила «весь подставляемый текст экранируется» —
    // строка сегодня статична и без интерполяции, но исключение без
    // техничного обоснования читается как недосмотр.
    printButtonLabel: escapeHtml(t('pdf.printButton')),
  });

  openPrintDocument(html, `analysis-${shortId}.html`);
}
